import { log } from '@ms-ows/logging'
import type {
  IIconOptions,
  IIconSubset,
  registerIcons,
} from '@uifabric/styling'
import type {
  Coachmark,
  createTheme,
  Customizer,
  DefaultPalette,
  DirectionalHint,
  getColorFromString,
  getContrastRatio,
  IButtonProps,
  ICoachmark,
  ICustomizations,
  ICustomizerProps,
  IImageStyles,
  IPalette,
  isDark,
  IStyle,
  ITeachingBubble,
  ITeachingBubbleProps,
  ITeachingBubbleStyleProps,
  ITeachingBubbleStyles,
  ITheme,
  styled,
  TeachingBubble,
  TeachingBubbleContent,
  ThemeGenerator,
  themeRulesStandardCreator,
} from 'office-ui-fabric-react'
import type { setDefaultTarget } from 'office-ui-fabric-react/lib/components/Layer/Layer.notification'
import type { createElement, useEffect, useMemo, useRef, useState } from 'react'
import type { render, unmountComponentAtNode } from 'react-dom'

import { getPageContext } from '../office-web-surfaces/state'

export type FabricDependencies = {
  createElement: typeof createElement
  createTheme: typeof createTheme
  Coachmark: typeof Coachmark
  Customizer: typeof Customizer
  DefaultPalette: typeof DefaultPalette
  DirectionalHint: typeof DirectionalHint
  getColorFromString: typeof getColorFromString
  getContrastRatio: typeof getContrastRatio
  isDark: typeof isDark
  registerIcons: typeof registerIcons
  render: typeof render
  setDefaultTarget?: typeof setDefaultTarget
  styled: typeof styled
  TeachingBubble: typeof TeachingBubble
  TeachingBubbleContent: typeof TeachingBubbleContent
  ThemeGenerator: typeof ThemeGenerator
  themeRulesStandardCreator: typeof themeRulesStandardCreator
  unmountComponentAtNode: typeof unmountComponentAtNode
  useEffect: typeof useEffect
  useMemo: typeof useMemo
  useRef: typeof useRef
  useState: typeof useState
}

export type DirectionalHintKeys = keyof typeof DirectionalHint

export type {
  IButtonProps,
  ICoachmark,
  ICustomizations,
  ICustomizerProps,
  IIconOptions,
  IIconSubset,
  IImageStyles,
  IPalette,
  IStyle,
  ITeachingBubble,
  ITeachingBubbleProps,
  ITeachingBubbleStyleProps,
  ITeachingBubbleStyles,
  ITheme,
}

const loadFromWindow = (): FabricDependencies | undefined => {
  if (!('React' in window && 'ReactDOM' in window && 'Fabric' in window)) {
    return
  }

  // tslint:disable: no-shadowed-variable
  const { createElement, useEffect, useMemo, useRef, useState } = window.React
  const { render, unmountComponentAtNode } = window.ReactDOM
  const {
    Coachmark,
    Customizer,
    DefaultPalette,
    DirectionalHint,
    TeachingBubble,
    TeachingBubbleContent,
    ThemeGenerator,
    createTheme,
    isDark,
    getContrastRatio,
    getColorFromString,
    registerIcons,
    styled,
    themeRulesStandardCreator,
    // tslint:disable-next-line: no-any
  } = (window as any).Fabric as typeof import('office-ui-fabric-react')
  // tslint:enable: no-shadowed-variable

  return {
    Coachmark,
    Customizer,
    DefaultPalette,
    DirectionalHint,
    TeachingBubble,
    TeachingBubbleContent,
    ThemeGenerator,
    createElement,
    createTheme,
    getColorFromString,
    getContrastRatio,
    isDark,
    registerIcons,
    render,
    styled,
    themeRulesStandardCreator,
    unmountComponentAtNode,
    useEffect,
    useMemo,
    useRef,
    useState,
  }
}

const inferCdnPath = () => {
  const scripts = document.getElementsByTagName('script')

  // tslint:disable-next-line: prefer-for-of
  for (let i = 0; i < scripts.length; i++) {
    const base = /(.*\/)officewebsurfaces.core(\.min)?\.js/.exec(scripts[i].src)

    if (base) {
      return base[1]
    }
  }

  return ''
}

export const getFabricDependencies = () => {
  const ctx = getPageContext()

  if (!ctx.fabricDependencies) {
    ctx.fabricDependencies = loadFromWindow()
  }

  return ctx.fabricDependencies
}

declare let __webpack_public_path__: string
declare let __fabric_path__: string

export const loadFabricDependencies = async () => {
  const ctx = getPageContext()

  try {
    if (!getFabricDependencies()) {
      __webpack_public_path__ =
        ctx.fabricPackagePath ?? __fabric_path__ ?? inferCdnPath()

      if (!__webpack_public_path__.endsWith('/')) {
        __webpack_public_path__ += '/'
      }

      ctx.fabricDependencies = (
        await import(
          /* webpackChunkName: "officewebsurfaces.fabric" */ './load'
        )
      ).fabricDependencies
    }
  } catch (error) {
    log.error(
      0x235df39d /* tag_9x5o3 */,
      'failed to load fabric dependencies',
      error
    )
    throw error
  }

  return ctx.fabricDependencies!
}
